import { Link, router } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMyDayPlanStore, type PreviewRow } from "@/stores/myDayPlan";
import { csrfToken } from "@/lib/fetchSideEffect";
import { pluralRu } from "@/lib/plural";

// ─── helpers ────────────────────────────────────────────────────────────────

export function buildPayload(rows: PreviewRow[]) {
  const kept = rows.filter((r) => r.tag !== "remove" || r.keepDespiteRemove);
  const ordered_task_ids = kept.map((r) => r.taskId);
  const add_task_ids = kept.filter((r) => r.tag === "add").map((r) => r.taskId);
  const remove_task_ids = rows
    .filter((r) => r.tag === "remove" && !r.keepDespiteRemove)
    .map((r) => r.taskId);
  return { add_task_ids, remove_task_ids, ordered_task_ids };
}

// Whether Accept button should be enabled: at least one kept/added task
function hasEffectiveEntries(rows: PreviewRow[]): boolean {
  return rows.some((r) => r.tag !== "remove" || r.keepDespiteRemove);
}

// ─── SortablePreviewRow ──────────────────────────────────────────────────────

function SortablePreviewRow({ row }: { row: PreviewRow }) {
  const toggleKeepDespiteRemove = useMyDayPlanStore((s) => s.toggleKeepDespiteRemove);
  const removeFromPlan = useMyDayPlanStore((s) => s.removeFromPlan);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.taskId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tagClasses: Record<string, string> = {
    add: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
    keep: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
    remove: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  };

  const tagLabels: Record<string, string> = {
    add: "Добавить",
    keep: "Оставить",
    remove: "Убрать",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded bg-gray-50 dark:bg-gray-800 px-2 py-2"
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Перетащи, чтобы изменить порядок"
        className="mt-0.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
        </svg>
      </button>

      {/* Tag pill */}
      <span
        className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${tagClasses[row.tag] ?? ""}`}
      >
        {tagLabels[row.tag] ?? row.tag}
      </span>

      {/* Title + reason */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white break-words">{row.title}</p>
        {row.reason && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{row.reason}</p>
        )}
        {row.tag === "remove" && (
          <label className="flex items-center gap-1.5 mt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={row.keepDespiteRemove ?? false}
              onChange={() => toggleKeepDespiteRemove(row.taskId)}
              className="rounded text-blue-600 dark:bg-gray-700 cursor-pointer"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">Всё равно оставить в «Мой день»</span>
          </label>
        )}
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={() => removeFromPlan(row.taskId)}
        aria-label={`Убрать ${row.title}`}
        className="flex-shrink-0 text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 cursor-pointer mt-0.5"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ─── BuilderStage ────────────────────────────────────────────────────────────

function BuilderStage() {
  const strategy = useMyDayPlanStore((s) => s.strategy);
  const prompt = useMyDayPlanStore((s) => s.prompt);
  const loadingPrompt = useMyDayPlanStore((s) => s.loadingPrompt);

  const loadPromptStart = useMyDayPlanStore((s) => s.loadPromptStart);
  const loadPromptDone = useMyDayPlanStore((s) => s.loadPromptDone);
  const goToPaste = useMyDayPlanStore((s) => s.goToPaste);
  const close = useMyDayPlanStore((s) => s.close);

  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const ensurePrompt = useCallback(async (): Promise<string | null> => {
    if (prompt) return prompt;
    loadPromptStart();
    try {
      const res = await fetch("/my-day/plan_prompt", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
      });
      if (!res.ok) {
        loadPromptDone("", {}, {});
        return null;
      }
      const json = (await res.json()) as {
        prompt: string;
        task_index: Record<string, string>;
        tasks_meta: Record<string, { title: string; parent_title: string | null }>;
      };
      const meta = Object.fromEntries(
        Object.entries(json.tasks_meta).map(([id, m]) => [
          id,
          { title: m.title, parentTitle: m.parent_title },
        ])
      );
      loadPromptDone(json.prompt, json.task_index, meta);
      return json.prompt;
    } catch {
      loadPromptDone("", {}, {});
      return null;
    }
  }, [prompt, loadPromptStart, loadPromptDone]);

  const handleShowPrompt = useCallback(async () => {
    if (!showPrompt && !prompt) {
      await ensurePrompt();
    }
    setShowPrompt((v) => !v);
  }, [showPrompt, prompt, ensurePrompt]);

  const handleCopy = useCallback(async () => {
    const text = await ensurePrompt();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be denied; proceed anyway.
    }
    setCopied(true);
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      goToPaste();
    }, 1500);
  }, [ensurePrompt, goToPaste]);

  return (
    <div data-testid="my-day-plan-builder">
      {/* Strategy — read-only; edited in Settings → AI actions */}
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Стратегия
          </span>
          <Link
            href="/settings/ai_actions"
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer flex-shrink-0"
          >
            Изменить стратегию →
          </Link>
        </div>
        <div className="p-3">
          {strategy ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-h-32 overflow-auto">
              {strategy}
            </p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Стратегии пока нет — добавь её в Настройках, чтобы направлять ИИ.
            </p>
          )}
        </div>
      </div>

      {/* Show prompt toggle */}
      <div className="flex items-center justify-between mb-3 text-xs">
        <button
          type="button"
          onClick={handleShowPrompt}
          className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
        >
          {showPrompt ? "Скрыть промпт" : "Показать промпт"}
        </button>
        <span className="text-gray-400 dark:text-gray-500">
          Скопируй → вставь в любой ИИ
        </span>
      </div>

      {showPrompt && (
        <pre className="mb-3 max-h-48 overflow-auto whitespace-pre-wrap text-[11px] leading-snug bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded p-3 border border-gray-200 dark:border-gray-700">
          {loadingPrompt && !prompt ? "Загружаю промпт…" : (prompt || "Промпт ещё не сгенерирован.")}
        </pre>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={close}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5 cursor-pointer"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={loadingPrompt || copied}
          className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-default text-white px-3 py-1.5 rounded-md font-medium cursor-pointer"
        >
          {copied ? "✓ Скопировано" : loadingPrompt ? "Загрузка…" : "Скопировать промпт"}
        </button>
      </div>
    </div>
  );
}

// ─── PasteStage ──────────────────────────────────────────────────────────────

function PasteStage() {
  const response = useMyDayPlanStore((s) => s.response);
  const parseError = useMyDayPlanStore((s) => s.parseError);
  const setResponse = useMyDayPlanStore((s) => s.setResponse);
  const parse = useMyDayPlanStore((s) => s.parse);
  const backToBuilder = useMyDayPlanStore((s) => s.backToBuilder);
  const close = useMyDayPlanStore((s) => s.close);

  return (
    <div data-testid="my-day-plan-paste">
      <button
        type="button"
        onClick={backToBuilder}
        className="text-xs text-gray-500 dark:text-gray-400 hover:underline mb-2 cursor-pointer"
      >
        ← К промпту
      </button>
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder="Вставь сюда полный ответ ИИ — вместе с маркерами ===TASKS START=== и ===TASKS END===."
        className="w-full min-h-[200px] rounded border border-gray-200 dark:border-gray-700 focus:border-blue-500 outline-none bg-white dark:bg-gray-800 p-2 text-sm text-gray-900 dark:text-white"
        rows={8}
        autoFocus
      />
      {parseError === "no_markers" && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
          Не удалось найти список задач. Проверь, что в ответе ИИ есть маркеры{" "}
          <code className="mx-0.5">===TASKS START===</code> и{" "}
          <code className="mx-0.5">===TASKS END===</code>.
        </p>
      )}
      <div className="flex items-center justify-between mt-5">
        <button
          type="button"
          onClick={close}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={parse}
          disabled={response.trim().length === 0}
          className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-default text-white px-4 py-1.5 rounded-md font-medium cursor-pointer"
        >
          Разобрать
        </button>
      </div>
    </div>
  );
}

// ─── PreviewStage ─────────────────────────────────────────────────────────────

function PreviewStage() {
  const preview = useMyDayPlanStore((s) => s.preview);
  const ignoredCount = useMyDayPlanStore((s) => s.ignoredCount);
  const reorderPreview = useMyDayPlanStore((s) => s.reorderPreview);
  const backToPaste = useMyDayPlanStore((s) => s.backToPaste);
  const close = useMyDayPlanStore((s) => s.close);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = preview.findIndex((r) => r.taskId === active.id);
    const newIndex = preview.findIndex((r) => r.taskId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderPreview(oldIndex, newIndex);
  }

  const canAccept = hasEffectiveEntries(preview);
  const payload = buildPayload(preview);

  function handleAccept() {
    router.post("/my-day/plan_apply", payload, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: close,
    });
  }

  return (
    <div data-testid="my-day-plan-preview">
      {ignoredCount > 0 && (
        <div className="mb-3 rounded-md bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 px-3 py-2 text-xs text-yellow-800 dark:text-yellow-200">
          Пропущено {ignoredCount}{" "}
          {pluralRu(ignoredCount, ["предложение ИИ", "предложения ИИ", "предложений ИИ"])} (не
          совпали с твоими задачами).
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={preview.map((r) => r.taskId)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {preview.map((row) => (
              <SortablePreviewRow key={row.taskId} row={row} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="flex items-center justify-between mt-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={close}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={backToPaste}
            className="text-sm text-gray-500 dark:text-gray-400 hover:underline cursor-pointer"
          >
            ← Назад
          </button>
        </div>
        <button
          type="button"
          onClick={handleAccept}
          disabled={!canAccept}
          className="text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-default text-white px-4 py-1.5 rounded-md font-medium cursor-pointer"
        >
          Применить
        </button>
      </div>
    </div>
  );
}

// ─── ErrorNoEntriesStage ──────────────────────────────────────────────────────

function ErrorNoEntriesStage() {
  const ignoredCount = useMyDayPlanStore((s) => s.ignoredCount);
  const backToBuilder = useMyDayPlanStore((s) => s.backToBuilder);
  const close = useMyDayPlanStore((s) => s.close);

  return (
    <div data-testid="my-day-plan-error-no-entries">
      <p className="text-sm text-gray-700 dark:text-gray-200 mb-2">
        ИИ ничего не предложил. Попробуй изменить стратегию или добавить контекст.
      </p>
      {ignoredCount > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          (Пропущено {ignoredCount}{" "}
          {pluralRu(ignoredCount, ["число", "числа", "чисел"])} — не совпали с твоими задачами.)
        </p>
      )}
      <div className="flex items-center justify-end gap-3 mt-4">
        <button
          type="button"
          onClick={close}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={backToBuilder}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md font-medium cursor-pointer"
        >
          К промпту
        </button>
      </div>
    </div>
  );
}

// ─── MyDayPlanModal ───────────────────────────────────────────────────────────

export default function MyDayPlanModal() {
  const open = useMyDayPlanStore((s) => s.open);
  const stage = useMyDayPlanStore((s) => s.stage);
  const close = useMyDayPlanStore((s) => s.close);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      data-testid="my-day-plan-backdrop"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">Спланировать день</h3>
          <button
            type="button"
            onClick={close}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {stage === "builder" && <BuilderStage />}
        {stage === "paste" && <PasteStage />}
        {stage === "preview" && <PreviewStage />}
        {stage === "errorNoEntries" && <ErrorNoEntriesStage />}
      </div>
    </div>
  );
}
